const TYPING = {
  char: 42,
  outputChar: 14,
  linePause: 220,
  blockPause: 520,
  loopPause: 5000,
}

const SCRIPT = [
  { kind: "command", text: 'plan "Weekly billing + Stripe webhook"' },
  { kind: "output", class: "text-zinc-400", text: "✓ Plan accepted — spawning 3 agents" },
  { kind: "gap" },
  { kind: "command", text: "agents spawn billing-api" },
  { kind: "output", class: "text-amber-300", text: "  → billing-api     running" },
  { kind: "command", text: "agents spawn stripe-hook" },
  { kind: "output", class: "text-amber-300", text: "  → stripe-hook     running" },
  { kind: "command", text: "agents spawn test-suite" },
  { kind: "output", class: "text-amber-300", text: "  → test-suite      running" },
  { kind: "gap" },
  { kind: "output", class: "text-zinc-500", text: "  billing-api    ████████░░  82%  app/services/billing.rb" },
  { kind: "output", class: "text-green-400", text: "  stripe-hook    ██████████  done  config/initializers/stripe.rb" },
  { kind: "output", class: "text-green-400", text: "  test-suite     ██████████  done  42 runs, 0 failures" },
  { kind: "gap" },
  { kind: "command", text: "review --merge billing-api" },
  { kind: "output", class: "text-green-400", text: "✓ PR #48 ready — deploying to production" },
  { kind: "output", class: "text-green-400", text: "✓ Live at https://app.example.com" },
  { kind: "prompt" },
]

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException("Aborted", "AbortError"))
    const id = window.setTimeout(resolve, ms)
    signal?.addEventListener(
      "abort",
      () => {
        window.clearTimeout(id)
        reject(new DOMException("Aborted", "AbortError"))
      },
      { once: true }
    )
  })
}

function createPromptLine() {
  const line = document.createElement("p")
  line.innerHTML =
    '<span class="text-green-500">➜</span> <span class="text-zinc-300">cowboycoder</span> <span class="text-zinc-500" data-command></span>'
  return line
}

function createCursor() {
  const cursor = document.createElement("span")
  cursor.className = "terminal-cursor text-zinc-300"
  cursor.textContent = "_"
  cursor.setAttribute("aria-hidden", "true")
  return cursor
}

async function typeInto(element, text, speed, signal) {
  for (const char of text) {
    signal.throwIfAborted()
    element.textContent += char
    await sleep(speed, signal)
  }
}

function renderStatic(body) {
  body.replaceChildren()

  for (const entry of SCRIPT) {
    if (entry.kind === "gap") {
      const gap = document.createElement("p")
      gap.className = "mt-4"
      gap.setAttribute("aria-hidden", "true")
      body.append(gap)
      continue
    }

    if (entry.kind === "prompt") {
      const line = createPromptLine()
      line.className = "mt-2"
      line.append(createCursor())
      body.append(line)
      continue
    }

    if (entry.kind === "command") {
      const line = createPromptLine()
      line.querySelector("[data-command]").textContent = ` ${entry.text}`
      body.append(line)
      continue
    }

    const line = document.createElement("p")
    line.className = entry.class
    line.textContent = entry.text
    body.append(line)
  }
}

async function runTerminalCycle(body, signal) {
  body.replaceChildren()

  for (const entry of SCRIPT) {
    if (entry.kind === "gap") {
      const gap = document.createElement("p")
      gap.className = "mt-4"
      gap.setAttribute("aria-hidden", "true")
      body.append(gap)
      await sleep(TYPING.linePause / 2, signal)
      continue
    }

    if (entry.kind === "prompt") {
      const line = createPromptLine()
      line.className = "mt-2"
      line.append(createCursor())
      body.append(line)
      break
    }

    if (entry.kind === "command") {
      const line = createPromptLine()
      const command = line.querySelector("[data-command]")
      const cursor = createCursor()
      line.append(cursor)
      body.append(line)

      await sleep(TYPING.blockPause, signal)
      await typeInto(command, ` ${entry.text}`, TYPING.char, signal)
      cursor.remove()
      await sleep(TYPING.linePause, signal)
      continue
    }

    const line = document.createElement("p")
    line.className = entry.class
    body.append(line)
    await typeInto(line, entry.text, TYPING.outputChar, signal)
    await sleep(TYPING.linePause / 2, signal)
  }
}

async function runTerminalLoop(body, signal) {
  while (!signal.aborted) {
    await runTerminalCycle(body, signal)
    await sleep(TYPING.loopPause, signal)
  }
}

function initHeroTerminal() {
  const terminal = document.querySelector("[data-hero-terminal]")
  const body = terminal?.querySelector("[data-hero-terminal-body]")
  if (!terminal || !body) return

  let started = false
  let controller = null

  const start = () => {
    if (started) return
    started = true

    if (prefersReducedMotion()) {
      renderStatic(body)
      return
    }

    controller = new AbortController()
    runTerminalLoop(body, controller.signal).catch((error) => {
      if (error.name !== "AbortError") throw error
    })
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        start()
        observer.disconnect()
      }
    },
    { threshold: 0.35, rootMargin: "0px 0px -5% 0px" }
  )

  observer.observe(terminal)

  window.addEventListener(
    "pagehide",
    () => {
      controller?.abort()
      observer.disconnect()
    },
    { once: true }
  )
}

document.addEventListener("DOMContentLoaded", initHeroTerminal)